import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import env from './utils/env';

// ── Types ────────────────────────────────────────────────────

interface LossDistribution {
    ead?: number;
    ead_fraction?: number;
    var_95?: number;
    var_99?: number;
    cvar_95?: number;
    cvar_99?: number;
    return_period_losses?: Record<string, number>;
}

interface HazardResult {
    hazard: string;
    engine: string;
    risk_level: string;
    expected_annual_damage?: number;
    impact_value?: number;
    detail?: string;
    error?: string;
    loss_distribution?: LossDistribution;
    financial?: {
        ead?: number;
        ead_fraction?: number;
        var_95?: number;
        var_99?: number;
        cvar_95?: number;
        cvar_99?: number;
        return_period_losses?: Record<string, number>;
    };
}

interface FinancialSummary {
    asset_value: number;
    discount_rate?: number;
    horizon_years?: number;
    total_ead: number;
    total_ead_fraction?: number;
    climate_var_npv?: number;
    climate_var_fraction?: number;
    climate_var_capped?: number;
    climate_var_exceeds_asset?: boolean;
    aggregate_var_95?: number;
    aggregate_var_99?: number;
    aggregate_cvar_95?: number;
    aggregate_cvar_99?: number;
    hazard_contributions?: Array<Record<string, any>>;
}

interface PropertyValuation {
    value_eur: number;
    tier: number;
    tier_label: string;
    confidence: number;
    source_details?: string;
    building_area_sqm?: number;
    price_per_sqm?: number;
    country_code?: string;
    city?: string;
}

interface ClimateRiskAssessment {
    _id: string;
    _address_id: string;
    address: string;
    lat: number;
    lon: number;
    asset_value?: number;
    scenario: string;
    year: number;
    asset_class?: string;
    asset_type?: string;
    results: HazardResult[];
    financial_summary?: FinancialSummary;
    property_valuation?: PropertyValuation;
    scenario_matrix?: Record<string, Record<string, HazardResult[]>>;
    tags?: string[];
    updated_at?: string;
    created_at?: string;
}

// ── Constants (matching the HTML report) ─────────────────────

const SSP_LABELS: Record<string, string> = {
    ssp126: 'SSP1-2.6  (Orderly, ~1.5°C)',
    ssp245: 'SSP2-4.5  (Disorderly, ~2.5°C)',
    ssp585: 'SSP5-8.5  (Hot House, ~3°C+)',
};

const PROJECTION_YEARS = [2030, 2040, 2050, 2060, 2080];

const NGFS_SCENARIOS = [
    { key: 'net_zero_2050', name: 'Net Zero 2050', ssp: 'ssp126', warming: '~1.5°C', risk_profile: 'Low physical / High transition', group: 'orderly' },
    { key: 'below_2c', name: 'Below 2°C', ssp: 'ssp126', warming: '<2°C', risk_profile: 'Low physical / Mod transition', group: 'orderly' },
    { key: 'low_demand', name: 'Low Demand', ssp: 'ssp126', warming: '~1.5°C', risk_profile: 'Low physical / Low transition', group: 'orderly' },
    { key: 'delayed_transition', name: 'Delayed Transition', ssp: 'ssp245', warming: '~2.5°C', risk_profile: 'Mod physical / High transition', group: 'disorderly' },
    { key: 'ndcs', name: 'NDCs (pledges)', ssp: 'ssp245', warming: '~2.5°C', risk_profile: 'Mod physical / Mod transition', group: 'disorderly' },
    { key: 'fragmented_world', name: 'Fragmented World', ssp: 'ssp585', warming: '~3°C+', risk_profile: 'Very high physical / Low transition', group: 'hot_house' },
    { key: 'current_policies', name: 'Current Policies', ssp: 'ssp585', warming: '~3°C+', risk_profile: 'Very high physical / Low transition', group: 'hot_house' },
    { key: 'current_policies_high', name: 'Current Policies (high)', ssp: 'ssp585', warming: '>4°C', risk_profile: 'Extreme physical / No transition', group: 'hot_house' },
];

// ── Helpers ──────────────────────────────────────────────────

function fmtEur(v: number | undefined | null): string {
    if (v === undefined || v === null) return '-';
    return 'EUR ' + v.toLocaleString('de-DE', { maximumFractionDigits: 0 });
}

function fmtPct(v: number | undefined | null, digits = 2): string {
    if (v === undefined || v === null) return '-';
    return (v * 100).toFixed(digits) + '%';
}

// ── Component ────────────────────────────────────────────────

const ClimateRiskDetailView: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const { token, authLoaded } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const mapRef = useRef<HTMLDivElement>(null);
    const mapInstanceRef = useRef<any>(null);

    const [assessment, setAssessment] = useState<ClimateRiskAssessment | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeSSP, setActiveSSP] = useState('ssp585');
    const [reportDownloading, setReportDownloading] = useState(false);

    // Fetch assessment
    useEffect(() => {
        if (!authLoaded || !id) return;
        setLoading(true);
        const headers: Record<string, string> = { 'x-api-key': env.apiKey };
        if (token) headers['Authorization'] = `Bearer ${token}`;

        axios.get(`${env.apiUrl}/climate-risk/${id}`, { headers })
            .then(res => setAssessment(res.data))
            .catch(err => setError(err.response?.status === 404 ? t('climateRisk.notFound') : t('common.error')))
            .finally(() => setLoading(false));
    }, [authLoaded, token, id, t]);

    // Initialize Leaflet map
    useEffect(() => {
        if (!assessment || !mapRef.current || mapInstanceRef.current) return;

        const loadLeaflet = async () => {
            // Dynamically load Leaflet
            if (!(window as any).L) {
                const css = document.createElement('link');
                css.rel = 'stylesheet';
                css.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
                document.head.appendChild(css);

                await new Promise<void>((resolve) => {
                    const script = document.createElement('script');
                    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
                    script.onload = () => resolve();
                    document.head.appendChild(script);
                });
            }

            const L = (window as any).L;
            const map = L.map(mapRef.current).setView([assessment.lat, assessment.lon], 14);

            const satellite = L.tileLayer(
                'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
                { attribution: 'Esri World Imagery', maxZoom: 19 }
            );
            const streets = L.tileLayer(
                'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
                { attribution: '© OpenStreetMap contributors', maxZoom: 19 }
            );

            satellite.addTo(map);
            L.control.layers({ 'Satellite': satellite, 'Street Map': streets }).addTo(map);

            const marker = L.marker([assessment.lat, assessment.lon]).addTo(map);
            marker.bindPopup(
                `<strong>${assessment.address}</strong><br>` +
                `${assessment.lat.toFixed(4)}°N, ${assessment.lon.toFixed(4)}°E<br>` +
                `Asset: ${fmtEur(assessment.asset_value)}`
            ).openPopup();

            mapInstanceRef.current = map;
        };

        loadLeaflet();

        return () => {
            if (mapInstanceRef.current) {
                mapInstanceRef.current.remove();
                mapInstanceRef.current = null;
            }
        };
    }, [assessment]);

    // Build risk matrix from scenario_matrix or flat results
    const getResultsForSSP = useCallback((ssp: string, year: number): HazardResult[] => {
        if (assessment?.scenario_matrix?.[ssp]?.[String(year)]) {
            return assessment.scenario_matrix[ssp][String(year)];
        }
        // Fallback: use flat results if the scenario matches
        if (assessment && assessment.scenario === ssp && assessment.year === year) {
            return assessment.results;
        }
        return [];
    }, [assessment]);

    const getAvailableSSPs = useCallback((): string[] => {
        if (assessment?.scenario_matrix) {
            return Object.keys(assessment.scenario_matrix).sort();
        }
        return [assessment?.scenario || 'ssp585'];
    }, [assessment]);

    // Build hazard map across years for the active SSP
    const getHazardMatrix = useCallback(() => {
        const ssps = getAvailableSSPs();
        const ssp = ssps.includes(activeSSP) ? activeSSP : ssps[0];
        const hazardMap = new Map<string, { hazard: string; engine: string; years: Record<number, HazardResult> }>();

        for (const yr of PROJECTION_YEARS) {
            const results = getResultsForSSP(ssp, yr);
            for (const r of results) {
                const key = r.hazard + '|' + r.engine;
                if (!hazardMap.has(key)) {
                    hazardMap.set(key, { hazard: r.hazard, engine: r.engine, years: {} });
                }
                hazardMap.get(key)!.years[yr] = r;
            }
        }

        // If no scenario_matrix, build from flat results for the stored year
        if (hazardMap.size === 0 && assessment) {
            for (const r of assessment.results) {
                const key = r.hazard + '|' + r.engine;
                if (!hazardMap.has(key)) {
                    hazardMap.set(key, { hazard: r.hazard, engine: r.engine, years: {} });
                }
                hazardMap.get(key)!.years[assessment.year] = r;
            }
        }

        return hazardMap;
    }, [activeSSP, assessment, getAvailableSSPs, getResultsForSSP]);

    const handleDownloadReport = useCallback(async () => {
        if (!assessment) return;
        setReportDownloading(true);
        try {
            const headers: Record<string, string> = { 'x-api-key': env.apiKey };
            if (token) headers['Authorization'] = `Bearer ${token}`;

            const res = await axios.get(
                `${env.apiUrl}/climate-risk/${assessment._id}/report`,
                { headers },
            );
            if (res.data?.url) {
                const link = document.createElement('a');
                link.href = res.data.url;
                link.download = `climate_risk_report_${assessment._address_id}.html`;
                link.rel = 'noopener noreferrer';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else {
                alert(t('climateRisk.reportNotFound'));
            }
        } catch (err: any) {
            const status = err.response?.status;
            if (status === 404) {
                alert(t('climateRisk.reportNotFound'));
            } else {
                alert(t('common.error'));
            }
        } finally {
            setReportDownloading(false);
        }
    }, [assessment, token, t]);

    // ── Render ───────────────────────────────────────────────

    if (loading) {
        return (
            <div className="container">
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>{t('common.loading')}</p>
                </div>
            </div>
        );
    }

    if (error || !assessment) {
        return (
            <div className="container">
                <div className="empty-state">
                    <span className="material-icons" style={{ fontSize: 48 }}>error_outline</span>
                    <p>{error || t('climateRisk.notFound')}</p>
                    <button className="btn btn-primary" onClick={() => navigate('/climate-risk')}>
                        {t('common.back')}
                    </button>
                </div>
            </div>
        );
    }

    const fs = assessment.financial_summary;
    const pv = assessment.property_valuation;
    const ssps = getAvailableSSPs();
    const hazardMatrix = getHazardMatrix();
    const yearsToShow = assessment.scenario_matrix ? PROJECTION_YEARS : [assessment.year];

    return (
        <div className="cr-report">
            {/* ── Header (matching HTML report gradient) ── */}
            <header className="cr-header">
                <div className="cr-container">
                    <div className="cr-header-actions">
                        <button className="cr-back-btn" onClick={() => navigate('/climate-risk')}>
                            <span className="material-icons">arrow_back</span>
                            {t('climateRisk.backToList')}
                        </button>
                        <button
                            className="cr-download-btn"
                            onClick={handleDownloadReport}
                            disabled={reportDownloading}
                            title={t('climateRisk.downloadReport')}
                        >
                            <span className="material-icons">
                                {reportDownloading ? 'hourglass_empty' : 'download'}
                            </span>
                            {t('climateRisk.downloadReport')}
                        </button>
                    </div>
                    <h1>ICAAP Climate Physical Risk Assessment</h1>
                    <div className="cr-meta-grid">
                        <div>
                            <span className="cr-meta-label">Address</span>
                            <span>{assessment.address}</span>
                        </div>
                        <div>
                            <span className="cr-meta-label">Coordinates</span>
                            <span>{assessment.lat.toFixed(4)}°N, {assessment.lon.toFixed(4)}°E</span>
                        </div>
                        <div>
                            <span className="cr-meta-label">Asset Value</span>
                            <span>
                                {fmtEur(assessment.asset_value)}
                                {pv && pv.tier > 1 && ` (estimated, Tier ${pv.tier}, ${Math.round(pv.confidence * 100)}% conf.)`}
                            </span>
                        </div>
                        <div>
                            <span className="cr-meta-label">Asset Class</span>
                            <span>{assessment.asset_type || 'Buildings/Residential'}</span>
                        </div>
                        <div>
                            <span className="cr-meta-label">Scenarios</span>
                            <span>{ssps.length} SSP pathways × {yearsToShow.length} time horizons</span>
                        </div>
                        {pv && pv.tier > 1 && pv.source_details && (
                            <div>
                                <span className="cr-meta-label">Valuation</span>
                                <span>{pv.source_details}</span>
                            </div>
                        )}
                    </div>
                </div>
            </header>

            <div className="cr-container">
                {/* ── Map Card ── */}
                <div className="cr-card">
                    <div className="cr-card-header">
                        <span className="material-icons">location_on</span>
                        Location Verification
                    </div>
                    <div className="cr-card-body" style={{ padding: 0 }}>
                        <div ref={mapRef} style={{ height: 420, width: '100%' }} />
                    </div>
                </div>

                {/* ── Risk Matrix Card with SSP tabs ── */}
                <div className="cr-card">
                    <div className="cr-card-header">
                        <span className="material-icons">grid_on</span>
                        Physical Risk Projections by NGFS Scenario
                    </div>
                    {ssps.length > 1 && (
                        <div className="cr-tabs">
                            {ssps.map(ssp => (
                                <button
                                    key={ssp}
                                    className={`cr-tab-btn ${activeSSP === ssp ? 'active' : ''}`}
                                    onClick={() => setActiveSSP(ssp)}
                                >
                                    {SSP_LABELS[ssp] || ssp}
                                </button>
                            ))}
                        </div>
                    )}
                    <div className="cr-card-body" style={{ overflowX: 'auto' }}>
                        <table className="cr-risk-table">
                            <thead>
                                <tr>
                                    <th>Hazard</th>
                                    <th>Engine</th>
                                    {yearsToShow.map(yr => (
                                        <th key={yr} style={{ textAlign: 'center' }}>
                                            {yr}
                                            {assessment.scenario_matrix && (
                                                <br />
                                            )}
                                            {assessment.scenario_matrix && (
                                                <span style={{ fontSize: '0.7rem', opacity: 0.6 }}>~{yr - 2026}y</span>
                                            )}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {Array.from(hazardMatrix.values()).map((entry, idx) => (
                                    <tr key={idx}>
                                        <td className="cr-hazard-name">{entry.hazard}</td>
                                        <td className="cr-engine-name">{entry.engine}</td>
                                        {yearsToShow.map(yr => {
                                            const r = entry.years[yr];
                                            if (!r) {
                                                return (
                                                    <td key={yr} style={{ textAlign: 'center' }}>
                                                        <span className="cr-risk-badge" data-level="No Data">No Data</span>
                                                    </td>
                                                );
                                            }
                                            const iv = r.impact_value != null ? (r.impact_value * 100).toFixed(4) + '%' : 'n/a';
                                            return (
                                                <td key={yr} style={{ textAlign: 'center' }}>
                                                    <span
                                                        className="cr-risk-badge"
                                                        data-level={r.risk_level}
                                                        title={`${r.detail || ''}\nImpact: ${iv}`}
                                                    >
                                                        {r.risk_level}
                                                    </span>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {/* NGFS scenarios sharing this SSP */}
                        {(() => {
                            const sharing = NGFS_SCENARIOS.filter(s => s.ssp === activeSSP);
                            if (sharing.length === 0) return null;
                            return (
                                <div style={{ padding: '12px 12px 4px', fontSize: '0.8rem', color: '#718096' }}>
                                    <strong>NGFS scenarios using this pathway:</strong>{' '}
                                    {sharing.map(s => `${s.name} (${s.warming})`).join(', ')}
                                </div>
                            );
                        })()}
                    </div>
                </div>

                {/* ── Climate Value-at-Risk Card ── */}
                {fs && (
                    <div className="cr-card">
                        <div className="cr-card-header">
                            <span className="material-icons">account_balance</span>
                            Climate Value-at-Risk
                        </div>
                        <div className="cr-card-body" style={{ padding: 20 }}>
                            {/* Big numbers row */}
                            <div className="cr-fin-top">
                                <div>
                                    <div className="cr-fin-label">Climate VaR (NPV)</div>
                                    <div className="cr-fin-big" style={{ color: 'var(--cr-accent)' }}>
                                        {fmtEur(fs.climate_var_npv)}
                                    </div>
                                    {fs.climate_var_capped != null && fs.climate_var_exceeds_asset && (
                                        <div className="cr-fin-capped">
                                            Capped: {fmtEur(fs.climate_var_capped)}
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <div className="cr-fin-label">Total EAD</div>
                                    <div className="cr-fin-big">{fmtEur(fs.total_ead)}</div>
                                    {fs.total_ead_fraction != null && (
                                        <div style={{ fontSize: '0.85rem', color: 'var(--cr-text-sec)' }}>
                                            {fmtPct(fs.total_ead_fraction)} of asset value
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* VaR / CVaR boxes */}
                            <div className="cr-fin-boxes">
                                <div className="cr-fin-box">
                                    <div className="cr-fin-box-label">VaR 95%</div>
                                    <div className="cr-fin-box-val">{fmtEur(fs.aggregate_var_95)}</div>
                                </div>
                                <div className="cr-fin-box">
                                    <div className="cr-fin-box-label">VaR 99%</div>
                                    <div className="cr-fin-box-val">{fmtEur(fs.aggregate_var_99)}</div>
                                </div>
                                <div className="cr-fin-box">
                                    <div className="cr-fin-box-label">CVaR 95%</div>
                                    <div className="cr-fin-box-val">{fmtEur(fs.aggregate_cvar_95)}</div>
                                </div>
                                <div className="cr-fin-box">
                                    <div className="cr-fin-box-label">CVaR 99%</div>
                                    <div className="cr-fin-box-val">{fmtEur(fs.aggregate_cvar_99)}</div>
                                </div>
                            </div>

                            {/* Warning if exceeds asset */}
                            {fs.climate_var_exceeds_asset && (
                                <div className="cr-fin-warning">
                                    <strong>Warning:</strong> Climate VaR NPV exceeds 100% of asset value. The raw NPV is shown for transparency; the capped value reflects the maximum possible loss.
                                </div>
                            )}

                            {/* Parameters */}
                            <div className="cr-fin-params">
                                {fs.discount_rate != null && (
                                    <span>Discount rate: <strong>{fmtPct(fs.discount_rate)}</strong></span>
                                )}
                                {fs.horizon_years != null && (
                                    <span>Horizon: <strong>{fs.horizon_years} years</strong></span>
                                )}
                                <span>Asset value: <strong>{fmtEur(fs.asset_value)}</strong></span>
                            </div>

                            {/* Hazard contributions bar */}
                            {fs.hazard_contributions && fs.hazard_contributions.length > 0 && (
                                <div style={{ marginTop: 16 }}>
                                    <div style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: 8 }}>
                                        EAD Breakdown by Hazard
                                    </div>
                                    {fs.hazard_contributions
                                        .filter((h: any) => h.ead > 0)
                                        .sort((a: any, b: any) => b.ead - a.ead)
                                        .map((h: any, i: number) => {
                                            const pct = fs.total_ead > 0 ? (h.ead / fs.total_ead) * 100 : 0;
                                            return (
                                                <div key={i} className="cr-ead-bar-row">
                                                    <span className="cr-ead-bar-label">{h.hazard}</span>
                                                    <div className="cr-ead-bar-track">
                                                        <div
                                                            className="cr-ead-bar-fill"
                                                            style={{ width: `${Math.min(pct, 100)}%` }}
                                                        />
                                                    </div>
                                                    <span className="cr-ead-bar-value">{fmtEur(h.ead)}</span>
                                                </div>
                                            );
                                        })}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* ── Detailed Hazard Breakdown ── */}
                <div className="cr-card">
                    <div className="cr-card-header">
                        <span className="material-icons">table_chart</span>
                        Detailed Hazard Breakdown
                    </div>
                    <div style={{ padding: '12px 20px', fontSize: '0.82rem', color: 'var(--cr-text-sec)', borderBottom: '1px solid var(--cr-border)' }}>
                        Raw API output values and per-hazard financial risk metrics for full transparency.
                    </div>
                    <div className="cr-card-body" style={{ overflowX: 'auto' }}>
                        <table className="cr-risk-table">
                            <thead>
                                <tr>
                                    <th>Hazard</th>
                                    <th>Engine</th>
                                    <th>Risk Level</th>
                                    <th>Impact</th>
                                    <th>EAD</th>
                                    <th>VaR 95%</th>
                                    <th>VaR 99%</th>
                                    <th>CVaR 95%</th>
                                    <th>CVaR 99%</th>
                                    <th>Detail</th>
                                </tr>
                            </thead>
                            <tbody>
                                {assessment.results.map((r, idx) => {
                                    const fin = r.financial || r.loss_distribution;
                                    return (
                                        <tr key={idx}>
                                            <td className="cr-hazard-name">{r.hazard}</td>
                                            <td className="cr-engine-name">{r.engine}</td>
                                            <td>
                                                <span className="cr-risk-badge" data-level={r.risk_level}>
                                                    {r.risk_level}
                                                </span>
                                            </td>
                                            <td>{r.impact_value != null ? fmtPct(r.impact_value, 4) : '-'}</td>
                                            <td>{fmtEur(fin?.ead ?? r.expected_annual_damage)}</td>
                                            <td>{fmtEur(fin?.var_95)}</td>
                                            <td>{fmtEur(fin?.var_99)}</td>
                                            <td>{fmtEur(fin?.cvar_95)}</td>
                                            <td>{fmtEur(fin?.cvar_99)}</td>
                                            <td className="cr-detail-cell">
                                                {r.error ? (
                                                    <span style={{ color: 'var(--cr-red)' }}>{r.error}</span>
                                                ) : (
                                                    r.detail || '-'
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* ── NGFS Scenario Reference ── */}
                <div className="cr-card">
                    <div className="cr-card-header">
                        <span className="material-icons">info</span>
                        NGFS Scenario Reference
                    </div>
                    <div className="cr-card-body" style={{ padding: '0 20px 20px' }}>
                        <table className="cr-ref-table">
                            <thead>
                                <tr>
                                    <th>Scenario</th>
                                    <th>Group</th>
                                    <th>SSP</th>
                                    <th>Warming</th>
                                    <th>Risk Profile</th>
                                </tr>
                            </thead>
                            <tbody>
                                {NGFS_SCENARIOS.map(s => (
                                    <tr key={s.key} className={`cr-group-${s.group}`}>
                                        <td style={{ fontWeight: 500 }}>{s.name}</td>
                                        <td style={{ textTransform: 'capitalize' }}>{s.group.replace('_', ' ')}</td>
                                        <td>{s.ssp}</td>
                                        <td>{s.warming}</td>
                                        <td>{s.risk_profile}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* ── Methodology ── */}
                <div className="cr-card">
                    <details className="cr-methodology">
                        <summary>Methodology & Data Sources</summary>
                        <div className="cr-meth-content">
                            <h4>Engines</h4>
                            <ul>
                                <li><strong>physrisk</strong> (OS-Climate/FINOS) — Flood, heat stress, drought, water stress, fire. Data from WRI Aqueduct, CMIP6 via Amazon Sustainability Data Initiative.</li>
                                <li><strong>CLIMADA</strong> (ETH Zürich) — River flood (ISIMIP/CaMa-Flood), coastal flood (Aqueduct), tropical cyclones, European windstorms, wildfire. Probabilistic risk engine with event-based hazard sets and JRC depth-damage curves.</li>
                                <li><strong>WRI Aqueduct Floods v2</strong> (static fallback) — Riverine + coastal flood inundation depth at ~1 km resolution.</li>
                            </ul>
                            <h4>Risk Classification</h4>
                            <p>Impact values are classified as: <strong>Negligible</strong> (&lt;0.01%), <strong>Low</strong> (0.01–0.1%), <strong>Medium</strong> (0.1–0.5%), <strong>High</strong> (0.5–2%), <strong>Very High</strong> (&gt;2%) of asset value.</p>
                            <h4>NGFS Mapping</h4>
                            <p>NGFS scenarios are mapped to SSP pathways for physical risk queries: Orderly → SSP1-2.6, Disorderly → SSP2-4.5, Hot House World → SSP5-8.5.</p>
                            <h4>Limitations</h4>
                            <ul>
                                <li>physrisk may return "No Data" for certain hazard/scenario/location combinations; fallback sources fill gaps where available.</li>
                                <li>Resolution varies by source: physrisk ~50 km, CLIMADA river flood ~5-10 km, Aqueduct Floods ~1 km.</li>
                                <li>Socio-economic change (adaptation, urbanisation) is not modelled.</li>
                            </ul>
                        </div>
                    </details>
                </div>

                {/* ── Footer timestamps ── */}
                <footer className="cr-footer">
                    {assessment.updated_at && (
                        <span>Last updated: {new Date(assessment.updated_at).toLocaleDateString()}</span>
                    )}
                    <span> · ICAAP Climate Risk Tool · Engines: physrisk + CLIMADA</span>
                </footer>
            </div>
        </div>
    );
};

export default ClimateRiskDetailView;
